import React, { useMemo } from 'react';
import { Form, Grid, Menu, Popup } from 'semantic-ui-react';
import { Field } from '../../interfaces/Field';
import { Entity } from '../../interfaces/Entity';
import { EntityMetadata as Metadata } from '../../interfaces/EntityMetadata';
import { ViewMode, getFormattedValue } from '../../utils/valueFormatUtils';
import {
  CustomModal,
  POSIcon,
  POSIconButton,
} from '@publicisgroupe/p-os-components';
import { FieldsType } from '../../constants/FieldsType';
import { ConditionalFieldsOperators } from '../../enums/ConditionalFieldsOperators';

import './ViewModal.less';
import { clsx } from 'clsx';

type Props = {
  entityData: Entity;
  metadata: Metadata;
  open: boolean;
  onClose?: () => void;
  onEdit: (clickedItem: Entity) => void;
  onDelete: (item: Entity) => void;
};

export const ViewModal: React.FC<Props> = ({
  entityData,
  metadata,
  open,
  onClose,
  onEdit,
  onDelete,
}) => {
  const filteredFields = useMemo(() => {
    return metadata.fields.filter((field: Field) => {
      let condition = true;
      if (field.conditionalField && field.conditionalField.fieldRef) {
        const { fieldRef, operator, value } = field.conditionalField;
        if (operator === ConditionalFieldsOperators.equals) {
          condition = entityData[fieldRef.toLowerCase()] === value;
        }
      }
      return !field.ignore && condition;
    });
  }, [metadata, entityData]);

  return (
    <CustomModal
      data-testid="view-modal"
      className="config-view-modal"
      open={open}
      onClose={onClose}
      centered
      size="large"
      header={
        <>
          View Data
          <Popup
            basic
            pinned
            hoverable
            position="bottom right"
            className="view-modal-popup"
            on={'click'}
            trigger={
              <POSIconButton
                iconOnly
                inverted
                floated="right"
                icon="eclipse"
                onClick={(e: any) => {
                  e.stopPropagation();
                }}
              />
            }
            content={
              <Menu vertical>
                <Menu.Item name="edit" onClick={() => onEdit(entityData)}>
                  Edit
                  <POSIcon icon="edit" />
                </Menu.Item>

                <Menu.Item name="delete" onClick={() => onDelete(entityData)}>
                  <POSIcon icon="delete" />
                  Delete
                </Menu.Item>
              </Menu>
            }
          />
        </>
      }
    >
      <CustomModal.Content>
        <Form>
          <Grid.Row columns={2}>
            {filteredFields.map((field: Field) => {
              const fieldKey =
                field.name.charAt(0).toLowerCase() + field.name.slice(1);
              const fieldValue = entityData[fieldKey] ?? '';
              const formattedValue = getFormattedValue(
                fieldValue,
                field,
                ViewMode.View
              );

              return (
                <Grid.Column
                  key={fieldKey}
                  className={clsx({
                    fluid: field.type === FieldsType.xdmContext,
                  })}
                >
                  <Form.Field>
                    <label>{field.name}</label>
                    <div
                      className={
                        field.type !== FieldsType.xdmContext ? 'xdm-view' : ''
                      }
                    >
                      {formattedValue}
                    </div>
                  </Form.Field>
                </Grid.Column>
              );
            })}
          </Grid.Row>
        </Form>
      </CustomModal.Content>
    </CustomModal>
  );
};
